import {
  useQueryClient,
  useQuery,
  useMutation,
  queryOptions,
} from "@tanstack/react-query";

import {
  scrape,
  searchImages,
  // getProjects,
  // getProject,
  // createProject,
  getDrNumbers,
  getAccounts,
} from "@app/services/zoltarDemoPilot";

export const demoPilotKey = {
  all: ["demoPilot"],
  projects: () => [...demoPilotKey.all, "projects"],
  project: (id) => [...demoPilotKey.projects(), id],
  drNumbers: (filter) => [...demoPilotKey.all, "drNumbers", { filter }],
  accounts: (filter) => [...demoPilotKey.all, "accounts", { filter }],
  scrapes: () => [...demoPilotKey.all, "scrapes"],
  scrape: (url, params) => [...demoPilotKey.scrapes(), { url, params }],
  searches: () => [...demoPilotKey.all, "searches"],
  search: (params) => [...demoPilotKey.searches(), { params }],
};

// export const useDemoPilotProjects = (options = {}) => {
//   return useQuery({
//     queryKey: demoPilotKey.projects(),
//     queryFn: ({ signal }) => getProjects({ signal }),
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     ...options,
//   });
// };

// export const useDemoPilotProject = (id, options = {}) => {
//   return useQuery({
//     queryKey: demoPilotKey.project(id),
//     queryFn: ({ signal }) => getProject(id, { signal }),
//     enabled: !!id,
//     ...options,
//   });
// };

// export const useCreateDemoPilotProject = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: (newProject, { signal }) =>
//       createProject(newProject, { signal }),
//     onMutate: async (newProject) => {
//       await queryClient.cancelQueries({ queryKey: demoPilotKey.projects() });

//       newProject.description = `Created with BeStory`;
//     },
//     onSettled: () => {
//       queryClient.invalidateQueries({ queryKey: demoPilotKey.projects() });
//     },
//   });
// };

export const useDrNumbers = (filter, options = {}) => {
  const trimmedFilter = filter.trim();
  return useQuery({
    queryKey: demoPilotKey.drNumbers(trimmedFilter),
    queryFn: ({ signal }) => getDrNumbers(trimmedFilter, { signal }),
    enabled: !!trimmedFilter && trimmedFilter.length >= 3,
    staleTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
};

export const useAccounts = (filter, options = {}) => {
  const trimmedFilter = filter.trim();
  return useQuery({
    queryKey: demoPilotKey.accounts(trimmedFilter),
    queryFn: ({ signal }) => getAccounts(trimmedFilter, { signal }),
    enabled: !!trimmedFilter && trimmedFilter.length >= 3,
    staleTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
};

export const scrapeQueryOptions = (
  url,
  params = {},
  options = {},
  { shouldParseAssets = false, dataParser } = {},
) =>
  queryOptions({
    queryKey: demoPilotKey.scrape(url, {
      ...params,
      shouldParseAssets,
      dataParser,
    }),
    queryFn: async (queryContext) => {
      const { signal } = queryContext;

      if (!shouldParseAssets) {
        return scrape(url, params, { signal });
      }

      const scrapeData = await scrape(url, params, { signal });

      scrapeData.assets = (scrapeData.assets || []).map((asset, idx) => {
        return {
          id: `asset-${idx}`,
          type: asset.data?.colors ? "color" : "image",
          ...asset.data,
        };
      });

      if (typeof dataParser === "function") {
        return dataParser(scrapeData);
      }

      return scrapeData;
    },
    enabled: !!url,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });

export const useScrape = (url = "", params = {}, options = {}) => {
  return useQuery(scrapeQueryOptions(url, params, options));
};

export const searchImagesQueryOptions = (params = {}, options = {}) =>
  queryOptions({
    queryKey: demoPilotKey.search(params),
    queryFn: async ({ signal }) => {
      const result = await searchImages(params, { signal });

      return (
        result?.results?.map((item, idx) => ({
          id: `${item.url}-${idx}`,
          type: "image",
          src: item.image,
          thumb: item.thumbnail,
          alt: item.title,
          width: item.width,
          height: item.height,
          // name: item.title,
          sourceUrl: item.url,
          isDisabled: false,
        })) ?? []
      );
    },
    enabled: !!params.q && Object.keys(params).length > 0,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });

export const useSearchImages = (params = {}, options = {}) => {
  return useQuery(searchImagesQueryOptions(params, options));
};
